import { useRef, useMemo } from "react";
import { App, Checkbox, TableColumnsType, TableProps } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import { useLocation, useNavigate } from "react-router-dom";
import VLTable from "views/uikit/VLTable";
import { ModelData } from "../types";
import { useModelTable } from "./useModelTable";
import TableFileIcon from "assets/icons/TableFileIcon";
import styles from "./style.module.scss";
import Moment from "react-moment";
import TextWithTooltip from "views/components/TextWithTooltip";
import VLToastComponent from "views/components/VLToastComponent";
import { toast } from "react-toastify";
import ExportIcon from "assets/icons/ExportIcon";
import { VLTableActionsConfig } from "views/uikit/VLTable/tableUtils";
import { TableFiltersProps } from "views/uikit/VLTable/TableFilters/TableFilters";
import TrashIcon from "assets/icons/TrashIcon";
import useModelFilters, { updateURLWithFilters } from "./useModelFilter";
import classNames from "classnames";
import { useUserConfig } from "hooks/useUserConfig";
import VLTooltip from "../../../uikit/VLTooltip";
import { NO_PERMISSION_TOOLTIP_TEXT } from "../../../../helpers/constants/tooltips";
import { useGetModelCatalogFilterOptionsApiV1ModelsCatalogFiltersGet } from "../../../../vl-api/vlComponents";
import {
  ModelCatalogSortField,
  SortOrder as APISortOrder,
} from "../../../../vl-api/vlSchemas";

const ModelTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastIdRef = useRef<any>(null);
  const { modal } = App.useApp();

  const filterOptionsResponse =
    useGetModelCatalogFilterOptionsApiV1ModelsCatalogFiltersGet({});

  const filterOptions = filterOptionsResponse?.data;

  const {
    filters: currentFilters,
    queryParams: apiQueryParams,
    availableFilters,
  } = useModelFilters(location.search, filterOptions);

  const {
    tableData,
    loading,
    handleExport,
    handleDelete,
    handleUpdateProduction,
    totalItemsCount,
  } = useModelTable(apiQueryParams);

  const handleTableFilterChange = async (filters: any) => {
    updateURLWithFilters(navigate, filters);
  };

  // Get current sort state from URL params
  const currentSortBy = apiQueryParams.sort_by as
    | ModelCatalogSortField
    | undefined;
  const currentSortOrder = apiQueryParams.sort_order as
    | APISortOrder
    | undefined;

  const handleSortChange: TableProps<ModelData>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortBy = sorter.columnKey as ModelCatalogSortField;
      let sortOrder: APISortOrder;

      // Check if clicking on the same column that's already sorted
      if (currentSortBy === sortBy) {
        // Toggle between asc and desc
        sortOrder = currentSortOrder === "asc" ? "desc" : "asc";
      } else {
        // New column clicked - default to desc
        sortOrder = "desc";
      }

      updateURLWithFilters(
        navigate,
        {
          ...apiQueryParams,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
        false,
      );
    } else if (!sorter || (Array.isArray(sorter) && sorter.length === 0)) {
      // Reset to default sort
      const { sort_by, sort_order, ...restParams } = apiQueryParams;
      updateURLWithFilters(
        navigate,
        {
          ...restParams,
          sort_by: "last_updated" as ModelCatalogSortField,
          sort_order: "desc" as APISortOrder,
        },
        false,
      );
    }
  };

  // Helper function to get sortOrder for a column
  const getSortOrder = (
    columnKey: ModelCatalogSortField,
  ): SortOrder | undefined => {
    if (currentSortBy === columnKey && currentSortOrder) {
      return currentSortOrder === "asc" ? "ascend" : "descend";
    }
    return undefined;
  };

  const { workspaces } = useUserConfig();
  const canManageModels = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return false;
    return workspaces.some(
      (ws) => (ws as any).permissions?.can_manage_models === true,
    );
  }, [workspaces]);

  const handleResultClick = (html_report_path?: string | null) => {
    if (!!html_report_path) {
      window.open(html_report_path, "_blank");
    }
  };

  const handleProductionUpdate = async (
    modelId: string,
    isProduction: boolean,
  ) => {
    try {
      await handleUpdateProduction(modelId, isProduction);
    } catch (err: any) {
      showErrorToast(
        "Oops! Something went wrong!",
        err ?? "Failed to update model production status.",
      );
    }
  };

  const handleProductionConfirm = (modelId: string) => {
    modal.confirm({
      title: "Confirm removal from production",
      content:
        "You are about to unmark this model as production. Are you sure?",
      width: 560,
      okText: "Confirm",
      cancelText: "Cancel",
      closable: true,
      onOk: () => handleProductionUpdate(modelId, false),
    });
  };

  const columns: TableColumnsType<ModelData> = [
    {
      title: "Model Name",
      dataIndex: "model_name",
      key: "model_name",
      width: 160,
      render: (text: string) => <TextWithTooltip text={text} />,
      sorter: true,
      sortOrder: getSortOrder("model_name"),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text} />,
      sorter: true,
      sortOrder: getSortOrder("type"),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text} />,
    },
    {
      title: "Dataset name",
      dataIndex: "dataset_name",
      key: "dataset_name",
      width: 140,
      ellipsis: true,
      render: (text: string) => <TextWithTooltip text={text} />,
      sorter: true,
      sortOrder: getSortOrder("dataset_name"),
    },
    {
      title: "Dataset ID",
      dataIndex: "dataset_id",
      key: "dataset_id",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text} />,
    },
    {
      title: "Job",
      key: "job_name",
      width: 114,
      render: (_, record) => {
        const jobName = record?.metadata?.["Job Name"] || "-";
        return <TextWithTooltip text={jobName} />;
      },
    },
    {
      title: "Setup",
      key: "setup_name",
      width: 114,
      render: (_, record) => {
        const setupName = record?.metadata?.["Setup Name"] || "-";
        return <TextWithTooltip text={setupName} />;
      },
    },
    {
      title: "Recipe list",
      key: "recipes",
      width: 114,
      render: (_, record) => {
        const recipesName = Array.isArray(record?.metadata?.["Recipes Name"])
          ? record?.metadata?.["Recipes Name"].join(", ")
          : "-";
        return <TextWithTooltip text={recipesName || ""} />;
      },
    },
    {
      title: "Tool list",
      key: "tool_list",
      width: 114,
      render: (_, record) => {
        const toolList = Array.isArray(record?.metadata?.["Tool List"])
          ? record?.metadata?.["Tool List"].join(", ")
          : "-";
        return <TextWithTooltip text={toolList || ""} />;
      },
    },
    {
      title: "Last Updated",
      dataIndex: "last_updated",
      key: "last_updated",
      width: 114,
      render: (dateString: string) => (
        <Moment format="YYYY-MM-D, h:mm" local={true} utc={true}>
          {dateString}
        </Moment>
      ),
      sorter: true,
      sortOrder: getSortOrder("last_updated"),
    },
    {
      title: "Results",
      key: "results",
      width: 80,
      render: (_, record) => {
        const hasReport = !!record.html_report_path;
        const resultsIconClass = classNames(styles.resultIcon, {
          [styles.disabled]: !hasReport,
        });
        return (
          <div
            className={resultsIconClass}
            onClick={() =>
              hasReport && handleResultClick(record.html_report_path)
            }
          >
            <TableFileIcon />
          </div>
        );
      },
    },
    {
      title: "Production",
      key: "is_production",
      width: 110,
      render: (_, record) => {
        const isProduction = record.is_production ?? false;
        return (
          <VLTooltip
            title={!canManageModels ? NO_PERMISSION_TOOLTIP_TEXT : null}
          >
            <Checkbox
              checked={isProduction}
              disabled={!canManageModels}
              onChange={async (e) => {
                if (e.target.checked) {
                  handleProductionUpdate(record.id, true);
                } else {
                  handleProductionConfirm(record.id);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </VLTooltip>
        );
      },
      sorter: true,
      sortOrder: getSortOrder("is_production"),
    },
  ];

  const showErrorToast = (title: string, description: string) => {
    toastIdRef.current = toast(
      <VLToastComponent
        title={title}
        status="ERROR"
        description={description}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      { autoClose: 7000, closeButton: false, closeOnClick: false },
    );
  };

  const handleDeleteModel = (modelId: string) => {
    modal.confirm({
      title: "Delete Model",
      content: (
        <>
          <p>Are you sure you want to delete this model?</p>
          <span>
            This will permanently remove it from the VL catalog. It will not
            delete any files from disk.
          </span>
        </>
      ),
      width: 560,
      okText: "Yes, delete",
      okButtonProps: { danger: true },
      closable: true,
      onOk: async () => {
        try {
          await handleDelete(modelId);
        } catch (err: any) {
          showErrorToast(
            "Oops! Something went wrong!",
            err ?? "Failed to delete model.",
          );
        }
      },
    });
  };

  const actionsConfig: VLTableActionsConfig<ModelData> = {
    items: (record) => [
      {
        key: "copy",
        label: "Export",
        onClick: () => handleExport(record.id),
        icon: <ExportIcon />,
        className: styles.actionItems,
      },
      ...(canManageModels
        ? [
            {
              key: "delete",
              label: "Delete",
              onClick: () => handleDeleteModel(record.id),
              icon: <TrashIcon />,
              className: styles.actionItems,
            },
          ]
        : []),
    ],
  };

  const tableFilters: TableFiltersProps = {
    availableFilters,
    filters: currentFilters,
    onTableFilterChange: handleTableFilterChange,
  };

  return (
    <div className={styles.modelTableContainer}>
      <VLTable<ModelData>
        columns={columns}
        dataSource={tableData}
        loading={loading}
        localeEmptyText={loading ? "Loading..." : "No training jobs found"}
        rowKey="id"
        size={"middle"}
        actions={actionsConfig}
        tableFilters={tableFilters}
        pagination={{
          total: totalItemsCount || 0,
          tooltip: "",
        }}
        onSortChange={handleSortChange}
      />
    </div>
  );
};

export default ModelTable;
